import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, trackByValue } from '@y42/angular/util';
import { Icon } from '@y42/core/icon';
import { ColumnState } from 'ag-grid-community';
import cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { QuickFilterModel } from '../model/quick-filter.model';

const defaultBackgroundColor = '#6BEDD4';
const defaultIcon = 'hashtag';
export interface ListViewAddEditQuickFilterDialogData {
  isEdit: boolean;
  quickFilter: QuickFilterModel;
}

@UntilDestroy()
@Component({
  selector: 'y42-list-view-add-edit-quick-filter-dialog',
  templateUrl: './list-view-add-edit-quick-filter-dialog.component.html',
  styleUrls: ['./list-view-add-edit-quick-filter-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListViewAddEditQuickFilterDialogComponent implements OnInit {
  constructor(
    private readonly dialogRef: MatDialogRef<ListViewAddEditQuickFilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: ListViewAddEditQuickFilterDialogData,
  ) {}

  private readonly filters$$ = new BehaviorSubject('');

  columnStates: ColumnState[] = [];
  readonly backgroundColors: string[] = [
    defaultBackgroundColor,
    '#6BD6ED',
    '#6B9FED',
    '#6E6BED',
    '#AC6BED',
    '#E36BED',
    '#ED6B9A',
    '#ED6B6B',
    '#ED926B',
    '#EDB96B',
    '#E0BB5C',
    '#D6E05C',
    '#AEE05C',
    '#8EE05C',
    '#AD3B2B',
    '#AD5A2B',
    '#2BAD8E',
    '#B1B1DA',
    '#2A2A86',
    '#9797FC',
    '#6EE05C',
    '#5CE091',
    '#5CE0D8',
    '#5CD0E0',
  ];

  readonly icons: IconName[] = [
    defaultIcon,
    'user',
    'users',
    'text',
    'smile',
    'file-alt',
    'share-alt',
    'money-bill-wave',
    'pen',
    'flag',
    'globe',
    'phone-laptop',
    'clipboard-user',
    'clipboard-list',
    'paperclip',
    'calendar-week',
    'calendar-alt',
    'chart-line',
    'chart-network',
    'chart-pie',
    'envelope',
    'paper-plane',
    'comment-dots',
    'comment-alt-lines',
  ];

  readonly trackByValue = trackByValue;

  userSelection: {
    color: string;
    icon: string;
  } = {
    color: defaultBackgroundColor,
    icon: defaultIcon,
  };

  readonly form = new FormGroup({
    name: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    background_color: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    icon: new FormControl('' as Icon, { validators: [Validators.required], nonNullable: true }),
  });

  isPrivate = false;
  title = '';
  groupDisplayText = '';

  readonly state = connectState(this, {
    icon: this.form.controls.icon.valueChanges.pipe(distinctUntilChanged()),
    filters: this.filters$$.pipe(map((filters) => (filters ? filters.replaceAll('_', ' ') : '/'))),
  });

  ngOnInit() {
    this.getFilters();
    this.getColumnState();
    this.setupForm();
  }

  setupForm() {
    const background_color = this.data.quickFilter.background_color || this.backgroundColors[0];
    const icon = this.data.quickFilter.icon || this.icons[0];
    const name = this.data.quickFilter.name || '';
    this.form.patchValue({
      name,
      background_color,
      icon,
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  submit() {
    const formValue = this.form.getRawValue();

    const filter = new QuickFilterModel(this.data.quickFilter);
    filter.name = formValue.name;
    filter.background_color = formValue.background_color;
    filter.icon = formValue.icon;
    filter.is_font_awesome = true;

    this.dialogRef.close({
      isPrivate: this.isPrivate,
      filter: cloneDeep(filter),
    });
  }

  getFilters() {
    this.filters$$.next(Object.keys(this.data.quickFilter.filterModel).join(', '));
  }

  getColumnState() {
    if (this.data.quickFilter.columnStates) {
      this.columnStates = this.data.quickFilter.columnStates;
      const groupedRows: string[] = [];
      this.columnStates.forEach((col) => {
        if (col.rowGroup === true) {
          groupedRows.push(col.colId!);
        }
      });
      this.groupDisplayText = groupedRows.join(', ');
    }
  }

  patchFormColor(color: string) {
    this.form.patchValue({ background_color: color });
  }

  patchFormIcon(icon: IconName) {
    this.form.patchValue({ icon });
  }
}
